import { useCallback, useEffect, useRef, useState } from "react";
import type { PremiseConfig } from "@shared/types/foyer";
import { migrateConfig } from "@shared/migrate-config";
import {
  getBinding,
  durableGetRaw,
  durableSetBounded,
  durableRemove,
} from "../lib/binding";
import { fetchBounded } from "../lib/boundedFetch";

/** Exported so checkout hygiene can drop cached premise content at turnover
 *  without duplicating the key format (see lib/checkoutWipe.ts). */
export const CONFIG_CACHE_PREFIX = "foyer.config.premise-";
const CACHE_PREFIX = CONFIG_CACHE_PREFIX;

/**
 * Ceiling on what we push into the native (SharedPreferences) store. A premise
 * config is normally tens of kilobytes; a property with a large app catalogue
 * and a photo-heavy menu can be several hundred. Past this we keep the
 * localStorage copy only - see {@link durableSetBounded}.
 */
const MAX_NATIVE_CONFIG_BYTES = 512 * 1024;

/** Backoff for re-reaching an API that isn't answering. */
const RETRY_BASE_MS = 3_000;
const RETRY_MAX_MS = 30_000;

/**
 * Safety-net re-fetch, independent of the realtime push channel (Web
 * PubSub/SSE). That channel is what normally drives `refresh()` on a
 * `config_changed` event, but a TV whose push connection has gone quiet -
 * dropped without a clean close, e.g. a flaky hotel Wi-Fi blip - has no other
 * way to notice it missed an update: the device heartbeat is a separate
 * channel and keeps reporting "online" the whole time. Without this timer a
 * stale config could persist until something else forces a reload. Deliberately
 * much slower than the retry backoff above: this is a last-resort catch-up,
 * not the primary sync path.
 */
const FALLBACK_POLL_MS = 5 * 60_000;

function cacheKey(id: string) {
  return `${CACHE_PREFIX}${id}`;
}

/**
 * On-disk shape of the last-good config.
 *
 * Wrapped in an envelope rather than stored bare so the TV can say HOW OLD the
 * content on screen is - which is the difference between an offline indicator a
 * receptionist can act on ("this room has been out of contact since 04:12") and
 * one that only says something is wrong. Bare configs written by older builds
 * are still read (the `config` key is simply absent), they just have no age.
 */
interface CachedConfig {
  savedAt: number;
  config: PremiseConfig;
}

export interface LoadedConfig {
  config: PremiseConfig;
  /** When the cache entry was written, or null for a pre-envelope entry. */
  savedAt: number | null;
}

function loadCached(id: string): LoadedConfig | null {
  try {
    const raw = durableGetRaw(cacheKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedConfig | PremiseConfig;
    const envelope = parsed as Partial<CachedConfig>;
    if (envelope && typeof envelope === "object" && envelope.config) {
      return {
        config: migrateConfig(envelope.config),
        savedAt: typeof envelope.savedAt === "number" ? envelope.savedAt : null,
      };
    }
    return { config: migrateConfig(parsed as PremiseConfig), savedAt: null };
  } catch {
    return null;
  }
}

function saveCached(id: string, cfg: PremiseConfig, savedAt: number): void {
  try {
    const payload: CachedConfig = { savedAt, config: cfg };
    durableSetBounded(
      cacheKey(id),
      JSON.stringify(payload),
      MAX_NATIVE_CONFIG_BYTES
    );
  } catch {
    // Storage may be unavailable (private mode etc.) - silent fallback.
  }
}

/** Drop a premise's last-good copy from BOTH stores (checkout hygiene). */
export function clearCachedConfig(id: string): void {
  durableRemove(cacheKey(id));
}

export interface ConfigState {
  config: PremiseConfig | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  /**
   * True when what is on screen came out of the last-good cache and the most
   * recent attempt to reach the API failed. This is the signal the offline
   * badge renders from - deliberately not `!navigator.onLine`, which on hotel
   * networks is true right up until the moment you try to use them.
   */
  offline: boolean;
  /**
   * When the config on screen was written to the cache (ms epoch), or null if
   * it never came from one. Survives a reboot, so a room that has been out of
   * contact overnight can say so.
   */
  savedAt: number | null;
  /** When the API last confirmed this config, in THIS session. */
  syncedAt: number | null;
  /**
   * The last attempt never reached an HTTP responder at all - DNS gone, WAN
   * cut, TLS refused by a captive portal. Distinguished from an API that
   * answered with a 404 or a 500, because the two mean opposite things to the
   * person reading the screen: one is "the network is down and this will fix
   * itself", the other is "this premise is misconfigured and somebody has to
   * do something".
   */
  unreachable: boolean;
}

export interface UseConfigArgs {
  premiseId?: string;
}

export function useConfig(arg: string | UseConfigArgs): ConfigState {
  const premiseId = typeof arg === "string" ? arg : (arg.premiseId ?? "");

  const [initial] = useState<LoadedConfig | null>(() => loadCached(premiseId));
  const [config, setConfig] = useState<PremiseConfig | null>(
    initial?.config ?? null
  );
  const [savedAt, setSavedAt] = useState<number | null>(
    initial?.savedAt ?? null
  );
  const [syncedAt, setSyncedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [unreachable, setUnreachable] = useState(false);

  // Consecutive failures, and the pending retry. Refs so a retry can be
  // rescheduled from inside `refresh` without making `refresh` itself change
  // identity - it is a dependency of the realtime effect in App.tsx, and an
  // identity that churned on every failure would tear the SSE stream down and
  // rebuild it once per retry, on exactly the network that is already failing.
  const failuresRef = useRef(0);
  const retryRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const aliveRef = useRef(true);
  /** Latest `refresh`, so the retry timer can call it without `refresh` having
   *  to reference itself (and without a dependency cycle). */
  const refreshRef = useRef<() => void>(() => {});

  const refresh = useCallback(async () => {
    if (retryRef.current) {
      clearTimeout(retryRef.current);
      retryRef.current = undefined;
    }
    // Flipped the moment an HTTP responder answers us, whatever it says. That
    // is the line between "the network is down" and "the API said no".
    let reached = false;
    try {
      // Premise fetches require the screen-token we got at pairing.
      const headers: Record<string, string> = {};
      const binding = getBinding();
      if (binding?.screenToken) {
        headers["X-Screen-Token"] = binding.screenToken;
      }
      // Always ask for the RENDERED config: the global catalogues (apps /
      // radio) merged in, exactly as a real screen sees them. A paired TV is
      // recognised by its screen-token; a CMS "Open preview" popup has no token
      // and would otherwise get the editor's premise-only view (stale stored
      // apps). `render=1` makes the server merge the catalogues for it too.
      // Bounded. A captive portal that accepts the connection and never answers
      // would otherwise leave this promise pending forever: the `catch` below
      // never runs, so the retry is never scheduled and the television never
      // recovers on its own. The abort lands in that `catch` with `reached`
      // still false, which is the correct reading - nothing answered us.
      const res = await fetchBounded(
        `/api/common/v1/premise/${premiseId}?render=1`,
        {
          headers,
        }
      );
      reached = true;
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      // A captive portal cannot substitute content on an https origin without a
      // trusted MITM certificate, so an interception surfaces here as a
      // connection failure or a redirect to the portal - either way this parse
      // throws rather than handing a scheme somebody's Wi-Fi terms as a config.
      const next = migrateConfig(await res.json());
      if (!aliveRef.current) return;
      const now = Date.now();
      setConfig(next);
      setSavedAt(now);
      setSyncedAt(now);
      setError(null);
      setUnreachable(false);
      failuresRef.current = 0;
      saveCached(premiseId, next, now);
    } catch (err) {
      if (!aliveRef.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setUnreachable(!reached);
      // Keep trying, with backoff. This - not a reboot, and not a staff member
      // walking to the room - is what makes recovery automatic when the WAN
      // comes back. `online` events help where the platform fires them; on hotel
      // TV firmware they frequently do not, so the timer is the load-bearing one.
      failuresRef.current += 1;
      const delay = Math.min(
        RETRY_MAX_MS,
        RETRY_BASE_MS * 2 ** (failuresRef.current - 1)
      );
      retryRef.current = setTimeout(() => {
        retryRef.current = undefined;
        refreshRef.current();
      }, delay);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [premiseId]);

  refreshRef.current = () => void refresh();

  useEffect(() => {
    aliveRef.current = true;
    void refresh();
    const onOnline = () => void refresh();
    window.addEventListener("online", onOnline);
    // Belt-and-suspenders re-fetch on a fixed timer - see FALLBACK_POLL_MS.
    // Runs regardless of whether the realtime channel looks healthy, because
    // "looks healthy" is exactly what a silently-dead connection also looks
    // like from here.
    const pollInterval = setInterval(() => {
      refreshRef.current();
    }, FALLBACK_POLL_MS);
    return () => {
      aliveRef.current = false;
      window.removeEventListener("online", onOnline);
      clearInterval(pollInterval);
      if (retryRef.current) clearTimeout(retryRef.current);
      retryRef.current = undefined;
    };
  }, [refresh]);

  return {
    config,
    loading,
    error,
    refresh,
    offline: error !== null && config !== null,
    savedAt,
    syncedAt,
    unreachable,
  };
}
